import React, {Component, createElement, FC, ForwardedRef, PropsWithChildren} from 'react';
import styled, {css} from 'styled-components';

import {
  annotationHtmlAttribute,
  annotationLinkedTypeAttribute,
} from '../../../../../../libs/shared/core/src/helpers/annotations/annotationHelpers';
import {containsSelection} from '../../../../../../libs/shared/core/src/helpers/browser/domHelpers';
import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {EventHandler} from '../../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {LoadedMessageModel} from '../../../../../../libs/shared/core/src/models/api/messageModel';
import {RecipientRolesEnum} from '../../../../../../libs/shared/core/src/models/api/recipientModel';
import {ChromelessButton} from '../../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {fontSizes} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis, noTextSelection, textSelection} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {renderStringWithAnnotations} from '../../../../../../srcCommon/formatters/markdown/renderStringWithAnnotations';
import {
  makeSearchableAttrsForRole,
  searchableAttrsSubject,
} from '../../../../../../srcCommon/inboxes/search/searchAttributesHelpers';
import {preventDragStart} from '../../../../../helpers/dragAndDropHelpers';
import {filterHighlightAnnotationsForMessageSubject} from '../../../../../helpers/models/annotationHelpers';
import {buildContactDisplayName} from '../../../../../helpers/models/contactHelpers';
import {VisualRecipientMap} from '../../../../../helpers/models/recipientHelpers';
import {HighlightAnnotationModel} from '../../../../../models/api/annotationModel';
import {
  AnnotationLinkedResourcesEnum,
  AnnotationSourcesEnum,
} from '../../../../../models/api/annotationTypes';
import {AnnotationsHighlightViewerInheritableProps} from '../../../../common/annotation/annotationsHighlightViewer';
import {AnnotationsHighlightViewerContainer} from '../../../../common/annotation/annotationsHighlightViewerContainer';
import {RecipientRoleFormatter} from '../../../../common/formatters/recipientRoleFormatter';
import {TextCopy} from '../../../../common/textCopy';
import {MessageViewerRecipientContainer} from './messageViewerRecipientContainer';
import {
  MessageViewerSender,
  MessageViewerSenderProps,
  StyledAutoResponsePill,
} from './sender/messageViewerSender';

/*
 * Props.
 */

export interface MessageViewerDetailsProps
  extends MessageViewerSenderProps,
    Pick<AnnotationsHighlightViewerInheritableProps, 'annotations' | 'hasMessageAnnotations'> {
  message: LoadedMessageModel;
  visualRecipients: VisualRecipientMap;
  isInDragRegion?: boolean;
  shouldShowSubjectWhenCollapsed?: boolean;
  scrollerVisibilityRef?: ForwardedRef<HTMLDivElement>;
  shouldHideSubject?: boolean;
  hasSmallFont?: boolean;
}

/*
 * Style shared.
 */

interface DetailsStyleProps {
  $areDetailsExpanded: boolean;
}
const StyledDetailsDiv = styled.div<DetailsStyleProps>`
  grid-area: content;
  min-width: 0;
  ${(p) => maybeAddTextSelection(p)}
`;

function maybeAddTextSelection(props: DetailsStyleProps) {
  if (!props.$areDetailsExpanded) {
    return '';
  }

  return css`
    ${StyledDetailsButton} {
      ${textSelection()};

      ${StyledSectionTitleDiv} {
        ${noTextSelection()};
      }
    }
  `;
}

const StyledDetailsButton = styled(ChromelessButton)`
  display: flex;
  flex-direction: column;

  padding: 3px 7px;
  border-radius: 6px;

  &:hover {
    background-color: ${(p) => p.theme.alphas.gray10};

    ${StyledAutoResponsePill} {
      background-color: ${(p) => p.theme.alphas.gray20};
    }
  }
`;

const StyledSectionTitleDiv = styled.div`
  flex-shrink: 0;
  line-height: 20px;
  color: ${(p) => p.theme.greys.shade70};
  margin-right: 4px;
`;

/*
 * Style collapsed.
 */

const StyledCollapsedDiv = styled.div`
  display: flex;
`;

const StyledCollapsedSectionContentDiv = styled.div`
  flex-shrink: 1;
  min-width: 0;
  line-height: 20px;
  color: ${(p) => p.theme.greys.shade70};
  ${ellipsis()};
`;

/*
 * Style expanded.
 */

const StyledExpandedDiv = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledExpandedSectionDiv = styled.div`
  display: flex;
`;

const StyledExpandedSectionContentDiv = styled.div`
  display: flex;
  flex-wrap: wrap;
  min-width: 0;
  color: ${(p) => p.theme.greys.shade80};
`;

const StyledSubjectDiv = styled.div`
  color: ${(p) => p.theme.message.header};
  line-height: 20px;
`;

const StyledRecipientDiv = styled.div<{$hasSmallFont: boolean}>`
  line-height: 20px;
  min-width: 0;
  ${ellipsis()};
  font-size: ${(p) => (p.$hasSmallFont ? fontSizes.small : fontSizes.medium)};
`;

/*
 * Component.
 */

interface MessageViewerDetailsState {
  areDetailsExpanded: boolean;
}

export class MessageViewerDetails extends Component<MessageViewerDetailsProps, MessageViewerDetailsState> {
  constructor(props: MessageViewerDetailsProps) {
    super(props);
    this.state = {areDetailsExpanded: false};
    this.hadSelection = false;
  }

  private hadSelection: boolean;

  /*
   * Event handlers.
   */

  private readonly onDetailsMouseDown: EventHandler = (e) => {
    this.hadSelection = containsSelection(e.currentTarget);
  };

  private readonly toggleAreDetailsExpanded: EventHandler = (e) => {
    if (e.defaultPrevented) {
      return;
    }
    e.stopPropagation();

    // If some text was selected, cancel.
    if (this.hadSelection) {
      return;
    }

    this.setState(({areDetailsExpanded}) => ({areDetailsExpanded: !areDetailsExpanded}));
  };

  /*
   * Render.
   */

  render() {
    const {areDetailsExpanded} = this.state;
    return (
      <StyledDetailsDiv
        draggable={this.props.isInDragRegion}
        onDragStart={preventDragStart}
        $areDetailsExpanded={areDetailsExpanded}
      >
        <TextCopy postProcess={(text) => text.replace(/\s+/g, ' ')}>
          <StyledDetailsButton onMouseDown={this.onDetailsMouseDown} onClick={this.toggleAreDetailsExpanded}>
            {this.renderDetails()}
          </StyledDetailsButton>
        </TextCopy>
      </StyledDetailsDiv>
    );
  }

  private renderDetails() {
    const {areDetailsExpanded} = this.state;
    return areDetailsExpanded ? this.renderExpandedDetails() : this.renderCollapsedDetails();
  }

  private renderCollapsedDetails() {
    return (
      <>
        <MessageViewerSender {...this.props} shouldHideClickableUntilHover />
        <StyledCollapsedDiv>
          {this.renderCollapsedRecipientBlock(RecipientRolesEnum.TO)}
          {this.renderCollapsedRecipientBlock(RecipientRolesEnum.CC)}
          {this.renderCollapsedRecipientBlock(RecipientRolesEnum.BCC)}
          {this.renderCollapsedRecipientBlock(RecipientRolesEnum.REPLY_TO)}
          {this.renderCollapsedRecipientBlock(RecipientRolesEnum.MENTION)}
        </StyledCollapsedDiv>
        {this.maybeRenderSubject()}
      </>
    );
  }

  private renderCollapsedRecipientBlock(role: RecipientRolesEnum) {
    // Nothing to render if we have no recipients.
    const recipients = this.props.visualRecipients[role];
    if (!recipients || recipients.length === 0) {
      return null;
    }

    // Render the recipients.
    const renderedRecipients = recipients.map(
      (r, index, array) => buildContactDisplayName(r) + (array[index + 1] ? ', ' : ''),
    );

    // Create searchable attributes.
    const searchableAttrs = makeSearchableAttrsForRole(role);

    return (
      <>
        <StyledSectionTitleDiv>
          <RecipientRoleFormatter role={role} />:
        </StyledSectionTitleDiv>
        <StyledCollapsedSectionContentDiv {...searchableAttrs}>
          {renderedRecipients}&nbsp;
        </StyledCollapsedSectionContentDiv>
      </>
    );
  }

  private renderExpandedDetails() {
    return (
      <>
        <MessageViewerSender {...this.props} isClickable />
        <StyledExpandedDiv>
          {this.renderExpandedRecipientBlock(RecipientRolesEnum.TO)}
          {this.renderExpandedRecipientBlock(RecipientRolesEnum.CC)}
          {this.renderExpandedRecipientBlock(RecipientRolesEnum.BCC)}
          {this.renderExpandedRecipientBlock(RecipientRolesEnum.REPLY_TO)}
          {this.renderExpandedRecipientBlock(RecipientRolesEnum.MENTION)}
          {this.maybeRenderSubject()}
        </StyledExpandedDiv>
      </>
    );
  }

  /* eslint react/no-array-index-key: 0 */
  private renderExpandedRecipientBlock(role: RecipientRolesEnum) {
    const {visualRecipients, panelId, searchQuery} = this.props;

    // Nothing to render if we have no recipients.
    const recipients = visualRecipients[role];
    if (!recipients || recipients.length === 0) {
      return null;
    }

    // Render the recipients.
    const renderedRecipients = recipients.map((r, index, array) => (
      <StyledRecipientDiv key={index} $hasSmallFont={Boolean(this.props.hasSmallFont)}>
        <MessageViewerRecipientContainer
          panelId={panelId}
          searchQuery={searchQuery}
          recipient={r}
          hasMoreRecipients={Boolean(array[index + 1])}
          searchableAttributes={makeSearchableAttrsForRole(role)}
        />
      </StyledRecipientDiv>
    ));

    // Render in a section, with a role.
    return (
      <StyledExpandedSectionDiv>
        <StyledSectionTitleDiv>
          <RecipientRoleFormatter role={role} />:
        </StyledSectionTitleDiv>
        <StyledExpandedSectionContentDiv>{renderedRecipients}</StyledExpandedSectionContentDiv>
      </StyledExpandedSectionDiv>
    );
  }

  private maybeRenderSubject() {
    const {subject} = this.props.message;
    if (!subject || this.props.shouldHideSubject) {
      return null;
    }

    if (this.props.shouldShowSubjectWhenCollapsed && !this.state.areDetailsExpanded) {
      return (
        <StyledCollapsedDiv>
          <StyledSectionTitleDiv>
            <Trans id="message-detail-subject">Subject</Trans>:
          </StyledSectionTitleDiv>
          <StyledCollapsedSectionContentDiv>{subject}</StyledCollapsedSectionContentDiv>
        </StyledCollapsedDiv>
      );
    }
    const filteredAnnotations = filterHighlightAnnotationsForMessageSubject(this.props.annotations);

    return (
      <StyledExpandedSectionDiv>
        <StyledSectionTitleDiv>
          <Trans id="message-detail-subject">Subject</Trans>:
        </StyledSectionTitleDiv>
        <StyledExpandedSectionContentDiv {...searchableAttrsSubject}>
          <AnnotationsHighlightViewerContainer
            annotationSource={AnnotationSourcesEnum.SUBJECT}
            conversationId={this.props.message.conversationId}
            hasMessageAnnotations={this.props.hasMessageAnnotations}
            panelId={this.props.panelId}
            annotations={this.props.annotations}
          >
            <StyledSubjectDiv>
              {renderStringWithAnnotations(subject, filteredAnnotations, Annotation)}
            </StyledSubjectDiv>
          </AnnotationsHighlightViewerContainer>
        </StyledExpandedSectionContentDiv>
      </StyledExpandedSectionDiv>
    );
  }
}

const Annotation: FC<PropsWithChildren<{annotation: HighlightAnnotationModel}>> = ({
  children,
  annotation,
}) => {
  const attributes = {
    class: 'front_annotation',
    'data-markjs': 'true',
    [annotationLinkedTypeAttribute]: annotation.linkedResourceType ?? AnnotationLinkedResourcesEnum.COMMENT,
    [annotationHtmlAttribute]: annotation.clientSideUid,
  };
  return createElement('front-annotation', attributes, children);
};
